<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'property',
    path: '/properties/:id',
    component: 'Property',
    params: [
      {
        name: 'id',
        prop: 'propertyId',
      },
    ],
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Property',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    propertyId: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref(null);
  const ux = ref({
    initialLoadSpinner: true,
  });
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_parentTo = computed(() => ({
    name: 'app.properties',
  }));

  const $data = computed(() => ({
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_parentTo: b_2248226175642056_parentTo.value,
  }));

  const $$load_item = () => {
    return $fn.value.fetch('read_property', {
      asResource: true,
      parameters: {
        id: props.propertyId,
        include_units: true,
        include_subtree: true,
      },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_parentTo', nodeId: '2248226175642056' },
    ],
    loaders: { item: $$load_item },
    ux: { initialLoadSpinner: true },
    dataRefs: {
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_parentTo,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [item.value.snapshot('name'), $fn.value.gettext('Property')],
        icon: $config.value.property.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.propertyId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('propertyId');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_h915ezvvo_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_h915ezvvo =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_h915ezvvo_getActionArgs,
    });
  const inst_evh_7315092382398562_close_coqrzttvr =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_h915ezvvo,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_coqrzttvr],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_817lqsqf0 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_py35p8zk6 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_817lqsqf0,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_py35p8zk6],
    event: 'reload',
  });
  const act_6466705138206980_reloadComponentData_n0bcg711n =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_6466705138206980_clickPreventStop_z2678wsen =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_n0bcg711n,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_6466705138206980_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6466705138206980_clickPreventStop_z2678wsen],
      event: 'click.prevent.stop',
    });
  const act_8320016629450276_closeComponent_boft6ygwp =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8320016629450276_success_tdl3t84gh =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_closeComponent_boft6ygwp,
      event: 'success',
      displayName: 'closeComponent',
    });
  const inst_evh_8320016629450276_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8320016629450276_success_tdl3t84gh],
    event: 'success',
  });
  const act_5477420070495986_reloadComponentData_4ua2uuw7q =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5477420070495986_reload_yx1synckq =
    new actionTypes.EventHandlerImpl({
      action: act_5477420070495986_reloadComponentData_4ua2uuw7q,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_5477420070495986_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5477420070495986_reload_yx1synckq],
    event: 'reload',
  });
  const act_6337614691287098_reloadComponentData_oidet6hmh =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6337614691287098_reload_lc5tay4vx =
    new actionTypes.EventHandlerImpl({
      action: act_6337614691287098_reloadComponentData_oidet6hmh,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6337614691287098_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6337614691287098_reload_lc5tay4vx],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_coqrzttvr,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_py35p8zk6,
    inst_evh_7315092382398562_reload,
    inst_evh_6466705138206980_clickPreventStop_z2678wsen,
    reload: inst_evh_6466705138206980_clickPreventStop_z2678wsen,
    inst_evh_6466705138206980_clickPreventStop,
    inst_evh_8320016629450276_success_tdl3t84gh,
    inst_evh_8320016629450276_success,
    inst_evh_5477420070495986_reload_yx1synckq,
    inst_evh_5477420070495986_reload,
    inst_evh_6337614691287098_reload_lc5tay4vx,
    inst_evh_6337614691287098_reload,
  };

  const evh_7315092382398562_close_coqrzttvr = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_coqrzttvr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_py35p8zk6 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_py35p8zk6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_clickPreventStop_z2678wsen = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6466705138206980_clickPreventStop_z2678wsen.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_success_tdl3t84gh = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_success_tdl3t84gh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_success = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5477420070495986_reload_yx1synckq = async (event, scope) => {
    return await actions.inst_evh_5477420070495986_reload_yx1synckq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5477420070495986_reload = async (event, scope) => {
    return await actions.inst_evh_5477420070495986_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6337614691287098_reload_lc5tay4vx = async (event, scope) => {
    return await actions.inst_evh_6337614691287098_reload_lc5tay4vx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6337614691287098_reload = async (event, scope) => {
    return await actions.inst_evh_6337614691287098_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Property"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <MaterialTabs
      :title="item.snapshot('name')"
      :parentText="gettext('Properties')"
      :parentTo="b_2248226175642056_parentTo"
      class="h-100"
      v-if="loaded"
    >
      <template #actions>
        <ItpButton
          variant="light"
          icon="fa-sync-alt"
          :text="gettext('Reload')"
          size="sm"
          :spinning="actions.reload.isRunning"
          @click.prevent.stop="
            evh_6466705138206980_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <BtnDeleteResource
          :resource="item"
          :dialogHeader="pgettext('Contract/DialogTitle', 'Delete property')"
          @success="evh_8320016629450276_success($event, {})"
        >
        </BtnDeleteResource>
      </template>
      <BTab :title="gettext('General')" class="container-lg float-start">
        <ItpRow>
          <ItpCol :w="6">
            <UcPropertyGeneralData
              :property="item"
              :reload="lastLoaded"
              class="pb-4"
              @reload="evh_5477420070495986_reload($event, {})"
            >
            </UcPropertyGeneralData>
            <UcPropertyUnits
              :parent="item"
              :reload="lastLoaded"
              class="pb-4"
              @reload="evh_6337614691287098_reload($event, {})"
            >
            </UcPropertyUnits>
          </ItpCol>
          <ItpCol :w="6">
            <UcCommentsList :parent="item" :reload="lastLoaded" class="pb-4">
            </UcCommentsList>
            <UcAttachmentsList :parent="item" :reload="lastLoaded" class="pb-4">
            </UcAttachmentsList>
          </ItpCol>
        </ItpRow>
      </BTab>
    </MaterialTabs>
  </ItpPage>
</template>
